import React, { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    AgreementStructure,
    AgreementStudentStructure,
    AgreementTeacherStructure,
    AgreementUpdateRequest
} from '../../shared/types/agreement';
import { updateAgreementFetch } from '../../app/axios/agreementFetches';
import HLButton, { HLButtonStyleType } from '../../shared/components/button/HLButton';
import HLErrorMessage from '../../shared/components/error-message/HLErrorMessage';
import HLModal, { HLModalSizeType } from '../../shared/components/modal/HLModal';
import { processFormServerError } from '../../shared/utils/processFormServerError';
import TeacherSelector from '../../components/selector/TeacherSelector';
import StudentSelector from '../../components/selector/StudentSelector';
import HLFormInput from "../../shared/components/input/HLFormInput";

interface UpdateAgreementFormData {
    name: string;
    selectedTeacher: AgreementTeacherStructure;
    selectedStudents: AgreementStudentStructure[];
}

interface UpdateAgreementModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    agreement: AgreementStructure;
    setAgreement: (agreement: AgreementStructure) => void;
}

const UpdateAgreementModal: FC<UpdateAgreementModalProps> = ({
                                                                 open,
                                                                 setOpen,
                                                                 agreement,
                                                                 setAgreement
                                                             }) => {

    const {
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        setValue,
        watch,
        formState: {errors, isSubmitting}
    } = useForm<UpdateAgreementFormData>({
        mode: "onChange"
    });

    useEffect(() => {
        if (open) reset()

        if (open && agreement) {
            setValue('name', agreement.name)
            setValue('selectedTeacher', agreement.teacher);
            setValue('selectedStudents', agreement.students);
        }
    }, [open, agreement, setValue]);

    const updateAgreement = async (data: UpdateAgreementFormData) => {
        if (!agreement) return

        try {
            const request: AgreementUpdateRequest = {
                teacherId: data.selectedTeacher.id,
                teacherRate: data.selectedTeacher.teacherRate,
                students: data.selectedStudents.map(selectedStudent => ({
                    studentId: selectedStudent.id,
                    lessonPrice: selectedStudent.lessonPrice,
                    availableLessons: selectedStudent.availableLessons
                }))
            };
            if (agreement.name !== data.name) request.name = data.name
            const updateAgreement = await updateAgreementFetch(agreement.id, request);

            setAgreement(updateAgreement)
            closeModal()
        } catch (err: any) {
            processFormServerError(err, setError);
        }
    }

    const closeModal = () => {
        reset()
        setOpen(false)
    }

    if (!agreement) return null

    return (
        <HLModal open={open} onClose={closeModal} title="Изменить договор" sizeType={HLModalSizeType.MEDIUM}>
            <form onSubmit={handleSubmit(updateAgreement)} className="columnContainer">
                <HLErrorMessage error={errors.root?.message}/>

                <HLFormInput
                    id="name"
                    {...register('name')}
                    placeholder="Название договора"
                    error={errors.name?.message}
                />

                <TeacherSelector
                    register={register}
                    setError={setError}
                    clearErrors={clearErrors}
                    setValue={setValue}
                    watch={watch}
                    errors={errors}
                />

                <StudentSelector
                    register={register}
                    setError={setError}
                    clearErrors={clearErrors}
                    setValue={setValue}
                    watch={watch}
                    errors={errors}
                />

                <div className="inlineContainer">
                    <HLButton
                        type="submit"
                        disabled={isSubmitting}
                    >
                        Изменить
                    </HLButton>

                    <HLButton
                        type="button"
                        onClick={closeModal}
                        styleType={HLButtonStyleType.GRAY}
                    >
                        Отмена
                    </HLButton>
                </div>
            </form>
        </HLModal>
    );
};

export default UpdateAgreementModal; 